import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useUser } from "./UserContext";
import {
  BillingCycle,
  PlanTier,
  ResourceAddonPack,
  ResourceType,
  SubscriptionRecord,
  SubscriptionSummary,
} from "../types/subscription";
import {
  getAvailablePlans,
  getResourceTypes,
  getUserSubscription,
  getUserOrgId,
  getOrganizationSubscription,
  getSubscriptionSummary,
  getAvailableAddonPacks,
  changeSubscriptionPlan,
  changeBillingCycle,
  cancelSubscription,
} from "../services/subscriptionService";
import { queryKeys } from "../utils/queryKeys";

interface SubscriptionContextValue {
  userSubscription: SubscriptionRecord | null;
  organizationSubscription: SubscriptionRecord | null;
  userOrgId: string | null;
  availablePlans: PlanTier[];
  resourceTypes: ResourceType[];
  availableAddons: ResourceAddonPack[];
  subscriptionSummary: SubscriptionSummary | null;
  isLoading: boolean;
  isChangingPlan: boolean;
  isChangingBillingCycle: boolean;
  isCanceling: boolean;
  loadUserSubscription: (userId: string) => Promise<void>;
  loadOrganizationSubscription: (orgId: string) => Promise<void>;
  loadSubscriptionSummary: (subscriptionId: string) => Promise<void>;
  changePlan: (subscriptionId: string, planId: string) => Promise<void>;
  changeCycle: (subscriptionId: string, cycle: BillingCycle) => Promise<void>;
  cancelSub: (subscriptionId: string, atPeriodEnd?: boolean) => Promise<void>;
  hasActiveSubscription: () => boolean;
  getFormattedNextBillingDate: () => string | null;
  getFormattedSubscriptionStatus: () => string;
}

const SubscriptionContext = createContext<SubscriptionContextValue | undefined>(
  undefined
);

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error(
      "useSubscription must be used within a SubscriptionProvider"
    );
  }
  return context;
}

interface SubscriptionProviderProps {
  children: ReactNode;
}

export function SubscriptionProvider({ children }: SubscriptionProviderProps) {
  const { user } = useUser();
  const queryClient = useQueryClient();

  const [organizationSubscription, setOrganizationSubscription] =
    useState<SubscriptionRecord | null>(null);
  const [subscriptionSummary, setSubscriptionSummary] =
    useState<SubscriptionSummary | null>(null);
  const [isChangingPlan, setIsChangingPlan] = useState(false);
  const [isChangingBillingCycle, setIsChangingBillingCycle] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);

  // Static subscription data — fetched once, shared across all consumers
  const { data: availablePlans = [], isLoading: plansLoading } = useQuery({
    queryKey: queryKeys.subscription.plans(),
    queryFn: getAvailablePlans,
    staleTime: 30 * 60 * 1000, // plans rarely change — 30 min
  });

  const { data: resourceTypes = [], isLoading: typesLoading } = useQuery({
    queryKey: queryKeys.subscription.resourceTypes(),
    queryFn: getResourceTypes,
    staleTime: 30 * 60 * 1000,
  });

  const { data: availableAddons = [], isLoading: addonsLoading } = useQuery({
    queryKey: queryKeys.subscription.addonPacks(),
    queryFn: getAvailableAddonPacks,
    staleTime: 30 * 60 * 1000,
  });

  // User subscription — re-fetches when user changes
  const { data: userSubscription = null, isLoading: subLoading } = useQuery({
    queryKey: queryKeys.user.subscription(user?.id ?? ''),
    queryFn: () => getUserSubscription(user!.id),
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch user's org membership to auto-load org subscription for members
  const { data: userOrgId = null } = useQuery({
    queryKey: ['user-org-membership', user?.id ?? ''],
    queryFn: () => getUserOrgId(user!.id),
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  // Auto-load org subscription when user is an org member
  useEffect(() => {
    if (!userOrgId) return;
    getOrganizationSubscription(userOrgId)
      .then((sub) => {
        if (sub) {
          setOrganizationSubscription(sub);
          getSubscriptionSummary(sub.id)
            .then(setSubscriptionSummary)
            .catch(console.error);
        }
      })
      .catch(console.error);
  }, [userOrgId]);

  const isLoading = plansLoading || typesLoading || addonsLoading || subLoading;

  // Manual loaders (called by consumers for org/summary data)
  const loadUserSubscription = async (userId: string) => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.user.subscription(userId) });
  };

  const loadOrganizationSubscription = async (orgId: string) => {
    try {
      const subscription = await getOrganizationSubscription(orgId);
      setOrganizationSubscription(subscription);
      if (subscription) {
        await loadSubscriptionSummary(subscription.id);
      }
    } catch (error) {
      console.error("Error loading organization subscription:", error);
    }
  };

  const loadSubscriptionSummary = async (subscriptionId: string) => {
    try {
      const summary = await getSubscriptionSummary(subscriptionId);
      setSubscriptionSummary(summary);
    } catch (error) {
      console.error("Error loading subscription summary:", error);
    }
  };

  const changePlan = async (subscriptionId: string, planId: string) => {
    try {
      setIsChangingPlan(true);
      const updatedSubscription = await changeSubscriptionPlan(subscriptionId, planId);

      if (updatedSubscription.owner_type === "user" && user?.id) {
        queryClient.setQueryData(queryKeys.user.subscription(user.id), updatedSubscription);
      } else {
        setOrganizationSubscription(updatedSubscription);
      }

      await loadSubscriptionSummary(subscriptionId);
    } catch (error) {
      console.error("Error changing subscription plan:", error);
      throw error;
    } finally {
      setIsChangingPlan(false);
    }
  };

  const changeCycle = async (subscriptionId: string, cycle: BillingCycle) => {
    try {
      setIsChangingBillingCycle(true);
      const updatedSubscription = await changeBillingCycle(subscriptionId, cycle);

      if (updatedSubscription.owner_type === "user" && user?.id) {
        queryClient.setQueryData(queryKeys.user.subscription(user.id), updatedSubscription);
      } else {
        setOrganizationSubscription(updatedSubscription);
      }
    } catch (error) {
      console.error("Error changing billing cycle:", error);
      throw error;
    } finally {
      setIsChangingBillingCycle(false);
    }
  };

  const cancelSub = async (subscriptionId: string, atPeriodEnd: boolean = true) => {
    try {
      setIsCanceling(true);
      const updatedSubscription = await cancelSubscription(subscriptionId, atPeriodEnd);

      if (updatedSubscription.owner_type === "user" && user?.id) {
        queryClient.setQueryData(queryKeys.user.subscription(user.id), updatedSubscription);
      } else {
        setOrganizationSubscription(updatedSubscription);
      }
    } catch (error) {
      console.error("Error canceling subscription:", error);
      throw error;
    } finally {
      setIsCanceling(false);
    }
  };

  const hasActiveSubscription = () => {
    const sub = userSubscription || organizationSubscription;
    if (!sub) return false;
    return sub.status === "active" || sub.status === "trialing";
  };

  const getFormattedNextBillingDate = () => {
    const sub = userSubscription || organizationSubscription;
    if (!sub || !sub.current_period_end) return null;
    return new Date(sub.current_period_end).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getFormattedSubscriptionStatus = () => {
    const sub = userSubscription || organizationSubscription;
    if (!sub) return "No Subscription";

    switch (sub.status) {
      case "active": return "Active";
      case "trialing": return "Trial";
      case "past_due": return "Past Due";
      case "canceled": return "Canceled";
      case "incomplete": return "Incomplete";
      case "incomplete_expired": return "Expired";
      case "unpaid": return "Unpaid";
      default: return "Unknown";
    }
  };

  const value = {
    userSubscription,
    organizationSubscription,
    userOrgId,
    availablePlans,
    resourceTypes,
    availableAddons,
    subscriptionSummary,
    isLoading,
    isChangingPlan,
    isChangingBillingCycle,
    isCanceling,
    loadUserSubscription,
    loadOrganizationSubscription,
    loadSubscriptionSummary,
    changePlan,
    changeCycle,
    cancelSub,
    hasActiveSubscription,
    getFormattedNextBillingDate,
    getFormattedSubscriptionStatus,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}
